import React from 'react';

import { Box, Grid } from '@chakra-ui/react';
import {
  REVIEW_PROVIDER_TYPE_LABELS,
  useReviewCreationData,
  useSelfFormFillingScoreSettings,
} from 'reducers/reviewCreationSlice';

import { FormVisibilitySettings, ShareIcon } from 'assets/assetsV1/iconComponents';

import { REVIEW_PROVIDER_TYPES } from 'constants/ProjectConstants';

import { Accordion } from 'uiToolkitV1/Accordion';
import { AccordionButton, AccordionItem2, AccordionPanel2 } from 'uiToolkitV1/Accordion/Accordion';
import { Divider } from 'uiToolkitV1/Divider';
import {
  ExpandablePaper,
  ExpandablePaperBody,
  ExpandablePaperHeader,
} from 'uiToolkitV1/ExpandablePaper/ExpandablePaper';
import { TextHeader4 } from 'uiToolkitV1/Typography';

import { ShareResult, useGetShareResults, useTemplateMetadata } from '../../Hooks/dataHooks/shareResultsDataHooks';
import { SkeletonLoaderMultiViewVariant } from '../../components/SkeletonLoaderReviewCreation';
import { Detail, RenderDetails } from './RenderDetails';

const ShareResultSetting: React.FC<{ setting: ShareResult }> = ({ setting }) => {
  const details: Detail[] = [
    {
      key: 'Who can publish the results to the employee?',
      value: setting.shareResultsSettings.isManagerPublishAllowed ? 'Admin or Manager' : 'Admin',
    },
  ];

  return <RenderDetails details={details} />;
};

const FormViewingVisibility: React.FC<{ reviewCycleId: string; setting: ShareResult }> = ({
  reviewCycleId,
  setting,
}) => {
  const hasSelf = useTemplateMetadata({
    reviewCycleId,
    feedbackFormId: setting.feedbackFormId,
    providerType: REVIEW_PROVIDER_TYPES.SELF,
  });
  const hasManager = useTemplateMetadata({
    reviewCycleId,
    feedbackFormId: setting.feedbackFormId,
    providerType: REVIEW_PROVIDER_TYPES.MANAGER,
  });
  const hasPeer = useTemplateMetadata({
    reviewCycleId,
    feedbackFormId: setting.feedbackFormId,
    providerType: REVIEW_PROVIDER_TYPES.PEER,
  });
  const hasUpward = useTemplateMetadata({
    reviewCycleId,
    feedbackFormId: setting.feedbackFormId,
    providerType: REVIEW_PROVIDER_TYPES.UPWARD,
  });
  const hasDLM = useTemplateMetadata({
    reviewCycleId,
    feedbackFormId: setting.feedbackFormId,
    providerType: REVIEW_PROVIDER_TYPES.DLM,
  });

  const selfGoalQuestions = hasSelf?.template?.templateSetting?.overallRatingDistribution?.PERFORMANCE > 0;
  const selfCompetencyQuestions = hasSelf?.template?.templateSetting?.overallRatingDistribution?.POTENTIAL > 0;
  const selfQNAQuestions = hasSelf?.template?.templateSetting?.overallRatingDistribution?.QNA > 0;
  const managerGoalQuestions = hasManager?.template?.templateSetting?.overallRatingDistribution?.PERFORMANCE > 0;
  const managerCompetencyQuestions = hasManager?.template?.templateSetting?.overallRatingDistribution?.POTENTIAL > 0;
  const managerQNAQuestions = hasManager?.template?.templateSetting?.overallRatingDistribution?.QNA > 0;

  const selfFormFillingScoreSettings = useSelfFormFillingScoreSettings({ feedbackFormId: setting.feedbackFormId });

  const applicableCategories = [];

  if (selfGoalQuestions && selfFormFillingScoreSettings?.isShowGoalRating) {
    applicableCategories.push('Overall Goals Score');
  }
  if (selfCompetencyQuestions && selfFormFillingScoreSettings?.isShowCompetencyRating) {
    applicableCategories.push('Overall Competency Score');
  }
  if (selfFormFillingScoreSettings?.isShowOverallRating) {
    applicableCategories.push('Overall Score');
  }

  const infoTextApplicableCategories = applicableCategories.join(', ');

  const answersSelfCanSee = [];
  if (hasSelf) {
    answersSelfCanSee.push(REVIEW_PROVIDER_TYPE_LABELS[REVIEW_PROVIDER_TYPES.SELF]);
  }
  if (hasManager && setting.shareResultsSettings.isShowManagerAnswers) {
    answersSelfCanSee.push(
      hasDLM
        ? `${REVIEW_PROVIDER_TYPE_LABELS[REVIEW_PROVIDER_TYPES.MANAGER]}/${
            REVIEW_PROVIDER_TYPE_LABELS[REVIEW_PROVIDER_TYPES.DLM]
          }`
        : REVIEW_PROVIDER_TYPE_LABELS[REVIEW_PROVIDER_TYPES.MANAGER]
    );
  }
  if (hasPeer && setting.shareResultsSettings.isShowPeerAnswers) {
    answersSelfCanSee.push(REVIEW_PROVIDER_TYPE_LABELS[REVIEW_PROVIDER_TYPES.PEER]);
  }
  if (hasUpward && setting.shareResultsSettings.isShowUpwardAnswers) {
    answersSelfCanSee.push(REVIEW_PROVIDER_TYPE_LABELS[REVIEW_PROVIDER_TYPES.UPWARD]);
  }

  const questionsSelfCanSee = [];
  if (selfGoalQuestions) {
    questionsSelfCanSee.push('Overall Goals Score');
  }
  if (selfCompetencyQuestions) {
    questionsSelfCanSee.push('Overall Competency Score');
  }
  if (selfQNAQuestions) {
    questionsSelfCanSee.push('Overall Q&A Score');
  }
  questionsSelfCanSee.push('Overall Score');

  const scoresManagerCanSee = [];
  if (hasSelf) {
    scoresManagerCanSee.push(REVIEW_PROVIDER_TYPE_LABELS[REVIEW_PROVIDER_TYPES.SELF]);
  }
  if (hasManager) {
    scoresManagerCanSee.push(REVIEW_PROVIDER_TYPE_LABELS[REVIEW_PROVIDER_TYPES.MANAGER]);
  }
  if (hasDLM) {
    scoresManagerCanSee.push(REVIEW_PROVIDER_TYPE_LABELS[REVIEW_PROVIDER_TYPES.DLM]);
  }

  const questionsManagerCanSee = [];
  if (managerGoalQuestions) {
    questionsManagerCanSee.push('Overall Goals Score');
  }
  if (managerCompetencyQuestions) {
    questionsManagerCanSee.push('Overall Competency Score');
  }
  if (managerQNAQuestions) {
    questionsManagerCanSee.push('Overall Q&A Score');
  }
  questionsManagerCanSee.push('Overall Score');

  const answersManagerCanSee = [];
  if (hasSelf) {
    answersManagerCanSee.push(REVIEW_PROVIDER_TYPE_LABELS[REVIEW_PROVIDER_TYPES.SELF]);
  }
  if (hasManager) {
    answersManagerCanSee.push(REVIEW_PROVIDER_TYPE_LABELS[REVIEW_PROVIDER_TYPES.MANAGER]);
  }
  if (hasDLM) {
    answersManagerCanSee.push(REVIEW_PROVIDER_TYPE_LABELS[REVIEW_PROVIDER_TYPES.DLM]);
  }
  if (hasPeer) {
    answersManagerCanSee.push(REVIEW_PROVIDER_TYPE_LABELS[REVIEW_PROVIDER_TYPES.PEER]);
  }
  if (hasUpward) {
    answersManagerCanSee.push(REVIEW_PROVIDER_TYPE_LABELS[REVIEW_PROVIDER_TYPES.UPWARD]);
  }

  const details: Detail[] = [
    {
      key: 'Form Viewing Visibilty',
      value: '',
      isHeader: true,
    },
  ];
  if (hasSelf) {
    details.push(
      {
        key: 'Can self see the Manager Score?',
        value: setting.shareResultsSettings.isShowManagerRating ? 'Show' : "Don't show",
      },
      {
        key: 'Whose answers do self see in the report card?',
        value: answersSelfCanSee.join(', '),
      },
      {
        key: 'Granularity of scores self can see in the shared report card:',
        value: infoTextApplicableCategories,
      }
    );
    if (hasPeer) {
      details.push({
        key: 'Can self see the names of the peer/upward reviewers?',
        value: setting.shareResultsSettings.isAnonymizePeers ? 'No' : 'Peer names are shown',
      });
    }
  }
  if (hasManager) {
    details.push(
      {
        key: 'Which scores can manager see in the shared report card',
        value: scoresManagerCanSee.join(', '),
      },
      {
        key: 'Granularity of scores manager can see in the shared report card:',
        value: questionsManagerCanSee.join(', '),
      },
      {
        key: 'Whose answers can manager see in the shared report card',
        value: answersManagerCanSee.join(', '),
      }
    );
    if (hasPeer) {
      details.push({
        key: 'Can the Manager/Dotted Line Manager see the names of the peer/upward reviewers?',
        // we only have this setting for manager view for now. Peer/upward names are always shown to managers
        value: 'Peer/Upward names are shown',
      });
    }
  }

  return <RenderDetails details={details} />;
};

const SettingPanel: React.FC<{
  reviewCycleId: string;
  setting: ShareResult;
}> = ({ setting, reviewCycleId }) => (
  <Accordion defaultIndex={0} allowMultiple allowToggle boxShadow="none !important" borderRadius="8px" mt={1}>
    <AccordionItem2 sxProps={{ borderBottom: 'none !important', p: '12px' }}>
      <AccordionButton p="20px 24px !important">
        <TextHeader4 bold>{setting?.feedbackFormName}</TextHeader4>
        {/* {settingsSameAsCohort && (
            <Flex ml={'auto'}>
              <TextBodyMedium mr={1}>Using same settings as</TextBodyMedium>
              <TextBodyMedium>{settingsSameAsCohort?.cohort?.name}</TextBodyMedium>
              <TooltipInfoV1 title="tooltip" ml={2} />
            </Flex>
          )} */}
      </AccordionButton>
      <AccordionPanel2 p={0}>
        <Box bg="white">
          <Divider borderColor="neutral.CFD7DF" mx={4} />
        </Box>
        <Grid templateColumns="1fr 2fr" rowGap={8} columnGap={8} p="24px 24px 28px" bgColor="white">
          <ShareResultSetting setting={setting} />
          <FormViewingVisibility reviewCycleId={reviewCycleId} setting={setting} />
        </Grid>
      </AccordionPanel2>
    </AccordionItem2>
  </Accordion>
);

export const ShareResults = () => {
  const { reviewCycleId } = useReviewCreationData();
  const { data: settings = [], isLoading } = useGetShareResults({ reviewCycleId });

  if (isLoading) {
    return <SkeletonLoaderMultiViewVariant />;
  }

  if (settings?.length === 1) {
    const setting = settings[0];

    return (
      <ExpandablePaper showLoader={isLoading}>
        <ExpandablePaperHeader
          title="Share Results"
          leftIconComponent={<FormVisibilitySettings boxSize="24px" color="icon.g2" />}
        />
        <ExpandablePaperBody>
          <Grid templateColumns="1fr 2fr" rowGap={8} columnGap={8} p="16px 8px 12px">
            <ShareResultSetting setting={setting} />
            <FormViewingVisibility reviewCycleId={reviewCycleId} setting={setting} />
          </Grid>
        </ExpandablePaperBody>
      </ExpandablePaper>
    );
  }

  return (
    <ExpandablePaper showLoader={isLoading}>
      <ExpandablePaperHeader
        title="Share Results"
        leftIconComponent={<ShareIcon boxSize="24px" color="icon.g2" />}
        borderBottom="none"
        marginBottom={0}
      />
      <ExpandablePaperBody p="0 4px 4px 4px" bgColor="neutral.EBEFF3" borderRadius="8px">
        {settings?.map((setting) => <SettingPanel reviewCycleId={reviewCycleId} setting={setting} />)}
      </ExpandablePaperBody>
    </ExpandablePaper>
  );
};
